import { useNavigate } from "react-router-dom";
import type { NewLead } from "@/api/types/newLead";
import { leadHeadline } from "./leadLabel";

interface NewLeadsBannerProps {
  count: number;
  latest: NewLead | undefined;
}

/**
 * LEAD-2 — the global "you have unactioned leads" banner. Sticky at the
 * very top of the authenticated app (see AppShell), visible only while at
 * least one inquiry sits in stage "new". It quiets itself the instant Chris
 * moves the last one off "new" — no dismiss button, on purpose: a lead that
 * gets manually dismissed and then forgotten is exactly the failure mode
 * LEAD-2 exists to close.
 */
export function NewLeadsBanner({ count, latest }: NewLeadsBannerProps) {
  const navigate = useNavigate();

  if (count <= 0) return null;

  const headline = leadHeadline(latest);
  const label = count === 1 ? "1 new lead" : `${count} new leads`;

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 border-b border-brass/40 bg-gradient-to-r from-espresso via-espresso-raised to-espresso text-cream shadow-lg"
    >
      {/* brass shelf-light along the top edge — ties the bar to the rail */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brass/70 to-transparent"
      />
      <div className="relative flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 sm:px-6">
        <span className="relative flex h-7 w-7 shrink-0 items-center justify-center">
          <span className="motion-safe:animate-ping absolute inline-flex h-5 w-5 rounded-full bg-brass/60" />
          <span className="relative inline-flex h-6 w-6 items-center justify-center rounded-full bg-brass/15 ring-1 ring-brass/50">
            <svg
              className="h-3.5 w-3.5 text-brass-bright"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 8a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
              <path d="M13.7 20a2 2 0 0 1-3.4 0" />
            </svg>
          </span>
        </span>

        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-cream sm:text-base">
          {label} — {headline}
        </p>

        <button
          type="button"
          onClick={() => navigate("/pipeline")}
          className="btn-sheen ml-auto shrink-0 rounded-md bg-brass px-3.5 py-1.5 text-sm font-bold text-espresso transition-colors hover:bg-brass-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass-bright focus-visible:ring-offset-2 focus-visible:ring-offset-espresso"
        >
          View leads
        </button>
      </div>
    </div>
  );
}
